/**
 * Full edit of a subscription, including one that has already been allocated.
 *
 * An allocated subscription owns units, so the server carries any change to the investor, the
 * unit count or the price through to the ownership position, the cap table event and the
 * instrument's units outstanding. The dialog says so plainly before the admin saves.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';

const PAYMENT_STATUSES = ['pending', 'received', 'partial', 'refunded'];
const PAYMENT_METHODS = ['bank_transfer', 'card', 'cash', 'crypto', 'other'];

const dateValue = (value) => (value ? String(value).slice(0, 10) : '');

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-foreground-muted">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-foreground-muted">{hint}</p>}
  </div>
);

export const SubscriptionEditDialog = ({ subscription, open, onOpenChange, onSaved }) => {
  const [parties, setParties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open || !subscription) return;
    setForm({
      party_id: subscription.party_id || subscription.investor_id || '',
      units_requested: String(subscription.units_requested ?? ''),
      units_approved: subscription.units_approved ? String(subscription.units_approved) : '',
      unit_price: String(subscription.unit_price ?? ''),
      payment_status: subscription.payment_status || 'pending',
      payment_method: subscription.payment_method || '',
      payment_reference: subscription.payment_reference || '',
      payment_date: dateValue(subscription.payment_date),
      // The recorded subscription date if one was set, otherwise the best evidence available
      subscription_date: dateValue(
        subscription.subscription_date
        || subscription.payment_date
        || subscription.allocated_at
        || subscription.created_at
      ),
      notes: subscription.notes || '',
    });
    api.get('/admin/v2/parties?limit=500')
      .then((res) => setParties(res.data.parties || res.data.items || []))
      .catch(() => setParties([]));
  }, [open, subscription]);

  if (!subscription) return null;

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const allocated = subscription.status === 'allocated';
  // A transfer leg is owned by its cap table event, so only its descriptive fields are editable
  const isTransfer = !!subscription.transfer_event_id;
  const units = Number(form.units_approved || form.units_requested || 0);
  const total = units * Number(form.unit_price || 0);
  const unitsMoved = units - Number(subscription.units_approved || subscription.units_requested || 0);
  const investorMoved = form.party_id && form.party_id !== (subscription.party_id || subscription.investor_id);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        party_id: isTransfer ? undefined : form.party_id || undefined,
        units_requested: isTransfer ? undefined : Number(form.units_requested) || undefined,
        units_approved: isTransfer || !form.units_approved ? undefined : Number(form.units_approved),
        unit_price: isTransfer ? undefined : form.unit_price || undefined,
        payment_status: form.payment_status || undefined,
        payment_method: form.payment_method || undefined,
        payment_reference: form.payment_reference || undefined,
        payment_date: form.payment_date ? `${form.payment_date}T00:00:00Z` : undefined,
        subscription_date: form.subscription_date ? `${form.subscription_date}T00:00:00Z` : undefined,
        notes: form.notes || undefined,
      };
      const res = await api.put(`/admin/v2/subscriptions/${subscription.id}`, payload);
      const moved = res.data?.reconciled;
      toast.success(
        moved
          ? `${res.data.message} · cap table adjusted by ${moved.units_delta >= 0 ? '+' : ''}${moved.units_delta} units`
          : res.data?.message || 'Subscription updated'
      );
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="subscription-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit subscription {subscription.code}</DialogTitle>
          <DialogDescription>
            Correct any detail of this subscription. Amounts recalculate from units and unit price.
          </DialogDescription>
        </DialogHeader>

        {allocated && (unitsMoved !== 0 || investorMoved) && (
          <div
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"
            data-testid="subscription-edit-reconcile-warning"
          >
            <Warning className="h-4 w-4 flex-shrink-0 mt-0.5" weight="duotone" />
            <span>
              This subscription is allocated. Saving will move{' '}
              {unitsMoved !== 0 && <strong>{unitsMoved > 0 ? `+${unitsMoved}` : unitsMoved} units</strong>}
              {unitsMoved !== 0 && investorMoved && ' and '}
              {investorMoved && <strong>the holding to the new investor</strong>}{' '}
              in the ownership position, the cap table event and the instrument&apos;s units outstanding.
            </span>
          </div>
        )}

        {isTransfer && (
          <div
            className="rounded-lg border border-black/[0.08] bg-black/[0.02] px-3 py-2.5 text-xs text-foreground-muted"
            data-testid="subscription-edit-transfer-note"
          >
            This row records a cap table transfer. Investor, units and price follow the transfer
            event, so change those by deleting and re-recording the transfer in the cap table.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Investor">
              <Select value={form.party_id} onValueChange={set('party_id')} disabled={isTransfer}>
                <SelectTrigger data-testid="subscription-edit-party">
                  <SelectValue placeholder="Select investor" />
                </SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.display_name || p.legal_name} {p.code ? `· ${p.code}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Units requested">
            <Input
              type="number" value={form.units_requested} disabled={isTransfer}
              onChange={(e) => set('units_requested')(e.target.value)}
              data-testid="subscription-edit-units"
            />
          </Field>
          <Field label="Units approved" hint="Drives the allocated holding; empty follows units requested">
            <Input
              type="number" value={form.units_approved} disabled={isTransfer}
              onChange={(e) => set('units_approved')(e.target.value)}
              data-testid="subscription-edit-units-approved"
            />
          </Field>
          <Field label="Unit price">
            <Input
              type="number" step="0.01" value={form.unit_price} disabled={isTransfer}
              onChange={(e) => set('unit_price')(e.target.value)}
              data-testid="subscription-edit-unit-price"
            />
          </Field>
          <Field label="Total amount" hint="Units × unit price">
            <Input
              readOnly value={total ? total.toLocaleString('en-GB', { minimumFractionDigits: 2 }) : ''}
              className="bg-black/[0.03]" data-testid="subscription-edit-total"
            />
          </Field>

          <Field
            label="Subscription date"
            hint="When the money actually moved - drives the raise date on project charts and every IRR"
          >
            <Input
              type="date" value={form.subscription_date}
              onChange={(e) => set('subscription_date')(e.target.value)}
              data-testid="subscription-edit-date"
            />
          </Field>
          <Field label="Payment date">
            <Input
              type="date" value={form.payment_date}
              onChange={(e) => set('payment_date')(e.target.value)}
              data-testid="subscription-edit-payment-date"
            />
          </Field>
          <Field label="Payment status">
            <Select value={form.payment_status} onValueChange={set('payment_status')}>
              <SelectTrigger data-testid="subscription-edit-payment-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Payment method">
            <Select value={form.payment_method} onValueChange={set('payment_method')}>
              <SelectTrigger data-testid="subscription-edit-payment-method">
                <SelectValue placeholder="Not set" />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Payment reference">
              <Input
                value={form.payment_reference}
                onChange={(e) => set('payment_reference')(e.target.value)}
                data-testid="subscription-edit-payment-reference"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Notes">
              <Input
                value={form.notes}
                onChange={(e) => set('notes')(e.target.value)}
                data-testid="subscription-edit-notes"
              />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="subscription-edit-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="subscription-edit-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionEditDialog;
